// Inventory Consumption Report: closed production operations → seed rows × period columns.
// Plan and Fact are plain sums over the operations; Difference = Fact − Plan
// (plus = overuse, minus = underuse).
import { periodOf } from './goodsOrderedReport';

export const TOLERANCE = 0.05;   // fact may differ from plan by ±5 % before it turns red

export function isOff(plan, fact) {
  if (!plan) return fact > 0.005;
  return Math.abs(fact - plan) / plan > TOLERANCE + 1e-9;
}

// Diff filter of the page: is the difference inside [from, to]? An empty bound is open.
// Compared on the value as it is shown (2 decimals), so -0.004 passes "from 0".
export function inDiffRange(diff, from, to) {
  const d = Math.round(diff * 100) / 100;
  const bound = (v) => (v === '' || v == null || isNaN(Number(v)) ? null : Number(v));
  const lo = bound(from);
  const hi = bound(to);
  return (lo == null || d >= lo) && (hi == null || d <= hi);
}

// A row passes the Diff filter when ANY Diff shown in it is inside the range: one of its period
// cells (day, week or month, whatever the columns are) or its Total. Judging the Total alone hid
// a seed with +55 lb on one day when its other days brought the sum under the bound.
export function rowInDiffRange({ cells, total }, from, to) {
  return [...Object.values(cells), total].some((c) => inDiffRange(c.fact - c.plan, from, to));
}

// ops: [{ id, recID, seedsID, seedName, date, plan, fact, ... }]
// → { periods: [{ key, label }], seeds: [{ seedsID, name, ops: [{ ...op, periodKey }] }] }
export function buildConsumptionReport(ops, period) {
  const periods = new Map();
  const seeds   = new Map();
  for (const o of ops) {
    const p = periodOf(o.date, period);
    periods.set(p.key, p.label);
    let s = seeds.get(o.seedsID);
    if (!s) { s = { seedsID: o.seedsID, name: o.seedName || `#${o.seedsID}`, ops: [] }; seeds.set(o.seedsID, s); }
    s.ops.push({ ...o, periodKey: p.key });
  }
  for (const s of seeds.values()) s.ops.sort((a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id));
  return {
    periods: [...periods.keys()].sort().map((key) => ({ key, label: periods.get(key) })),
    seeds:   [...seeds.values()].sort((a, b) => a.name.localeCompare(b.name)),
  };
}

// Σ plan / Σ fact of the given operations per period key, plus the overall total.
export function sumOps(ops) {
  const cells = {};
  const total = { plan: 0, fact: 0 };
  for (const o of ops) {
    if (!cells[o.periodKey]) cells[o.periodKey] = { plan: 0, fact: 0 };
    cells[o.periodKey].plan += o.plan;
    cells[o.periodKey].fact += o.fact;
    total.plan += o.plan;
    total.fact += o.fact;
  }
  return { cells, total };
}

// Quick search: seed or material name / ID (fertilizer materials are F1, F2…) match the whole row;
// an operation ID matches single operations.
// Returns the operations to show for the seed, or null when nothing matches.
export const seedMatches = (seed, q) => seed.name.toLowerCase().includes(q) || String(seed.seedsID).toLowerCase() === q;

export function visibleOps(seed, q) {
  if (!q || seedMatches(seed, q)) return seed.ops;
  const hits = seed.ops.filter((o) => o.id.toLowerCase().includes(q) || String(o.recID) === q);
  return hits.length ? hits : null;
}
